import { z } from 'zod';

// The model providers the platform can call. A tier names one; the LLM port picks the
// matching adapter. Adding a provider here is half the work — the other half is an
// adapter in `modules/agents/llm/adapters/`.
export const LLM_PROVIDERS = ['gemini', 'openai', 'anthropic'] as const;
export const LlmProviderSchema = z.enum(LLM_PROVIDERS);
export type LlmProvider = z.infer<typeof LlmProviderSchema>;

export const AGENT_MODEL_TIERS = [
  'large',
  'medium',
  'small',
  'claude-large',
  'claude-medium',
  'claude-small',
  'eval',
] as const;
export const AgentModelTierSchema = z.enum(AGENT_MODEL_TIERS);
export type AgentModelTier = z.infer<typeof AgentModelTierSchema>;

export const MODEL_TIERS = [...AGENT_MODEL_TIERS, 'image'] as const;
export const ModelTierSchema = z.enum(MODEL_TIERS);
export type ModelTier = z.infer<typeof ModelTierSchema>;

// A concrete model id. Deliberately unconstrained beyond length: authority over which
// ids exist belongs to the provider's live model list, not to this schema.
export const ModelIdSchema = z.string().min(1).max(120);
export type ModelId = z.infer<typeof ModelIdSchema>;

export const ModelTierBindingSchema = z.object({
  tier: ModelTierSchema,
  provider: LlmProviderSchema,
  modelId: ModelIdSchema,
  updatedAt: z.iso.datetime(),
  // Null on the seeded rows: no admin authored them.
  updatedBy: z.uuid().nullable(),
});
export type ModelTierBinding = z.infer<typeof ModelTierBindingSchema>;

export const ModelTierBindingListSchema = z.array(ModelTierBindingSchema);
export type ModelTierBindingList = z.infer<typeof ModelTierBindingListSchema>;

export const UpdateModelTierSchema = z.object({
  provider: LlmProviderSchema,
  modelId: ModelIdSchema,
});
export type UpdateModelTier = z.infer<typeof UpdateModelTierSchema>;

// One entry of a provider's live catalog, already stripped of any provider-specific prefix.
export const AvailableModelSchema = z.object({
  id: ModelIdSchema,
  displayName: z.string(),
  description: z.string(),
});
export type AvailableModel = z.infer<typeof AvailableModelSchema>;

export const AvailableModelListSchema = z.array(AvailableModelSchema);
export type AvailableModelList = z.infer<typeof AvailableModelListSchema>;
